import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	LinearProgress,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { serviceTypes, TypeServiceEnum } from 'features/salesOrder/types';
import { getSelectionHomogeneity, SelectedServiceRef } from 'features/salesOrder/helpers/servicesBulkSelection';
import {
	buildBulkEditPreview,
	bulkEditNeedsPut,
	BulkEditRowResult,
	executeServicesBulkEdit,
	hasBulkPatchValues,
	loadServicesForPreview,
	NotesPatchMode,
	ServiceLoadError,
	ServicesBulkEditPatch,
} from 'features/salesOrder/helpers/servicesBulkEdit';
import { getProviderId } from 'features/salesOrder/services';
import { BulkProviderAutocomplete } from './BulkProviderAutocomplete';

interface ServicesBulkEditDialogProps {
	open: boolean;
	selection: SelectedServiceRef[];
	serviceType: TypeServiceEnum;
	onClose: () => void;
	onCompleted: (results: BulkEditRowResult[]) => void;
}

type BulkStep = 'form' | 'preview' | 'executing' | 'done';

const MAX_BULK_SELECTION = 50;

export const ServicesBulkEditDialog = ({
	open,
	selection,
	serviceType,
	onClose,
	onCompleted,
}: ServicesBulkEditDialogProps): JSX.Element => {
	const { auth } = useSelector((state: RootState) => state);
	const [step, setStep] = useState<BulkStep>('form');
	const [loadingPreview, setLoadingPreview] = useState(false);
	const [progress, setProgress] = useState({ current: 0, total: 0 });
	const [results, setResults] = useState<BulkEditRowResult[]>([]);
	const [loadErrors, setLoadErrors] = useState<ServiceLoadError[]>([]);

	const [editInternalNotes, setEditInternalNotes] = useState(false);
	const [editVoucherNotes, setEditVoucherNotes] = useState(false);
	const [editProvider, setEditProvider] = useState(false);
	const [editEnable, setEditEnable] = useState(false);

	const [internalNotesMode, setInternalNotesMode] = useState<NotesPatchMode>('append');
	const [internalNotesValue, setInternalNotesValue] = useState('');
	const [voucherNotesMode, setVoucherNotesMode] = useState<NotesPatchMode>('append');
	const [voucherNotesValue, setVoucherNotesValue] = useState('');
	const [tariffProviderId, setTariffProviderId] = useState('');
	const [providerLabel, setProviderLabel] = useState('');
	const [enableValue, setEnableValue] = useState<'true' | 'false'>('true');

	const [servicesById, setServicesById] = useState<Map<string, import('features/salesOrder/types').IServices>>(
		new Map(),
	);

	const typeLabel = serviceTypes.find((item) => item.id === serviceType)?.name ?? serviceType;

	const { agencyCount, singleAgencyId } = useMemo(() => getSelectionHomogeneity(selection), [selection]);
	const isMultiAgency = agencyCount > 1;

	const patch = useMemo((): ServicesBulkEditPatch => {
		const next: ServicesBulkEditPatch = {};
		if (editInternalNotes && internalNotesValue.trim()) {
			next.internalNotes = { mode: internalNotesMode, value: internalNotesValue.trim() };
		}
		if (editVoucherNotes && voucherNotesValue.trim()) {
			next.voucherNotes = { mode: voucherNotesMode, value: voucherNotesValue.trim() };
		}
		if (editProvider && tariffProviderId) {
			next.tariffProviderId = tariffProviderId;
		}
		if (editEnable) {
			next.enable = enableValue === 'true';
		}
		return next;
	}, [
		editInternalNotes,
		internalNotesMode,
		internalNotesValue,
		editVoucherNotes,
		voucherNotesMode,
		voucherNotesValue,
		editProvider,
		tariffProviderId,
		editEnable,
		enableValue,
	]);

	const previewRows = useMemo(
		() => buildBulkEditPreview(selection, servicesById, patch, providerLabel || tariffProviderId),
		[selection, servicesById, patch, providerLabel, tariffProviderId],
	);

	const formValid = useMemo(() => {
		const anyFieldEnabled = editInternalNotes || editVoucherNotes || editProvider || editEnable;
		if (!anyFieldEnabled) return false;
		return hasBulkPatchValues(patch);
	}, [editInternalNotes, editVoucherNotes, editProvider, editEnable, patch]);

	const needsPut = useMemo(() => bulkEditNeedsPut(patch), [patch]);

	const missingHydrationCount = useMemo(() => {
		if (!needsPut) return 0;
		return selection.filter((item) => !servicesById.has(item.serviceId)).length;
	}, [needsPut, selection, servicesById]);

	const canApply = useMemo(() => {
		if (loadingPreview) return false;
		if (!needsPut) return true;
		return missingHydrationCount === 0;
	}, [loadingPreview, needsPut, missingHydrationCount]);

	useEffect(() => {
		if (!tariffProviderId) {
			setProviderLabel('');
			return;
		}
		let cancelled = false;
		getProviderId(tariffProviderId, singleAgencyId)
			.then((result) => {
				if (cancelled || !result.data) return;
				setProviderLabel(result.data.fantasyName || result.data.businessName || tariffProviderId);
			})
			.catch(() => {
				if (!cancelled) setProviderLabel(tariffProviderId);
			});
		return () => {
			cancelled = true;
		};
	}, [tariffProviderId, singleAgencyId]);

	useEffect(() => {
		if (!open) {
			setStep('form');
			setResults([]);
			setProgress({ current: 0, total: 0 });
			setServicesById(new Map());
			setLoadErrors([]);
			setEditInternalNotes(false);
			setEditVoucherNotes(false);
			setEditProvider(false);
			setEditEnable(false);
			setInternalNotesValue('');
			setVoucherNotesValue('');
			setTariffProviderId('');
			setProviderLabel('');
			return;
		}

		const load = async () => {
			setLoadingPreview(true);
			try {
				const { servicesById: map, errors } = await loadServicesForPreview(selection);
				setServicesById(map);
				setLoadErrors(errors);
			} catch {
				setLoadErrors([
					{
						serviceId: '',
						saleOrderId: '',
						message: 'Error inesperado al cargar los servicios',
					},
				]);
			} finally {
				setLoadingPreview(false);
			}
		};

		load();
	}, [open, selection]);

	const handleGoToPreview = () => {
		if (!formValid) return;
		setStep('preview');
	};

	const handleExecute = async () => {
		setStep('executing');
		setProgress({ current: 0, total: selection.length });

		const rowResults = await executeServicesBulkEdit(selection, patch, auth.country, (current, total) => {
			setProgress({ current, total });
		});

		setResults(rowResults);
		setStep('done');
		onCompleted(rowResults);
	};

	const successCount = results.filter((item) => item.status === 'success').length;
	const errorCount = results.filter((item) => item.status === 'error').length;

	return (
		<Dialog open={open} onClose={step === 'executing' ? undefined : onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				Edición masiva — {typeLabel} ({selection.length} servicios)
			</DialogTitle>

			<DialogContent dividers>
				{selection.length > MAX_BULK_SELECTION && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Seleccionaste {selection.length} servicios. Se recomienda un máximo de {MAX_BULK_SELECTION} por operación.
					</Alert>
				)}

				{loadingPreview && step === 'form' && (
					<Box display="flex" alignItems="center" gap={1} mb={2}>
						<CircularProgress size={20} />
						<Typography variant="body2">Cargando datos de servicios...</Typography>
					</Box>
				)}

				{isMultiAgency && step !== 'done' && (
					<Alert severity="info" sx={{ mb: 2 }}>
						Seleccionaste servicios de {agencyCount} agencias. Los cambios se aplicarán en cada una con su propio
						contexto.
					</Alert>
				)}

				{step === 'form' && (
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography variant="body2" color="text.secondary">
								Los cambios se aplicarán a todos los servicios seleccionados del mismo tipo. Activá los campos que
								querés modificar.
							</Typography>
						</Grid>

						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={editInternalNotes}
										onChange={(event) => setEditInternalNotes(event.target.checked)}
									/>
								}
								label="Notas internas"
							/>
							{editInternalNotes && (
								<Box sx={{ pl: 4, mt: 1 }}>
									<RadioGroup
										row
										value={internalNotesMode}
										onChange={(event) => setInternalNotesMode(event.target.value as NotesPatchMode)}
									>
										<FormControlLabel value="append" control={<Radio size="small" />} label="Agregar al final" />
										<FormControlLabel value="replace" control={<Radio size="small" />} label="Reemplazar" />
									</RadioGroup>
									<TextField
										fullWidth
										multiline
										minRows={2}
										size="small"
										label="Texto"
										value={internalNotesValue}
										onChange={(event) => setInternalNotesValue(event.target.value)}
										sx={{ mt: 1 }}
									/>
								</Box>
							)}
						</Grid>

						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={editVoucherNotes}
										onChange={(event) => setEditVoucherNotes(event.target.checked)}
									/>
								}
								label="Notas voucher"
							/>
							{editVoucherNotes && (
								<Box sx={{ pl: 4, mt: 1 }}>
									<RadioGroup
										row
										value={voucherNotesMode}
										onChange={(event) => setVoucherNotesMode(event.target.value as NotesPatchMode)}
									>
										<FormControlLabel value="append" control={<Radio size="small" />} label="Agregar al final" />
										<FormControlLabel value="replace" control={<Radio size="small" />} label="Reemplazar" />
									</RadioGroup>
									<TextField
										fullWidth
										multiline
										minRows={2}
										size="small"
										label="Texto"
										value={voucherNotesValue}
										onChange={(event) => setVoucherNotesValue(event.target.value)}
										sx={{ mt: 1 }}
									/>
								</Box>
							)}
						</Grid>

						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox checked={editProvider} onChange={(event) => setEditProvider(event.target.checked)} />
								}
								label="Proveedor de tarifa"
							/>
							{editProvider && (
								<Box sx={{ pl: 4, mt: 1, maxWidth: 480 }}>
									<BulkProviderAutocomplete
										value={tariffProviderId}
										onChange={setTariffProviderId}
										label="Nuevo proveedor de tarifa"
										agencyId={singleAgencyId}
										helperText={
											isMultiAgency
												? 'Lista de proveedores de tu contexto actual. El mismo ID se aplicará en todas las agencias seleccionadas.'
												: 'Elegí de la lista o escribí al menos 4 caracteres para buscar'
										}
									/>
								</Box>
							)}
						</Grid>

						<Grid item xs={12}>
							<FormControlLabel
								control={<Checkbox checked={editEnable} onChange={(event) => setEditEnable(event.target.checked)} />}
								label="Estado del servicio"
							/>
							{editEnable && (
								<Box sx={{ pl: 4, mt: 1, maxWidth: 240 }}>
									<FormControl fullWidth size="small">
										<InputLabel>Estado</InputLabel>
										<Select
											label="Estado"
											value={enableValue}
											onChange={(event) => setEnableValue(event.target.value as 'true' | 'false')}
										>
											<MenuItem value="true">Activar</MenuItem>
											<MenuItem value="false">Desactivar</MenuItem>
										</Select>
									</FormControl>
								</Box>
							)}
						</Grid>
					</Grid>
				)}

				{step === 'preview' && (
					<Box>
						{loadErrors.length > 0 && (
							<Alert severity="warning" sx={{ mb: 2 }}>
								No se pudieron cargar {loadErrors.length} servicio
								{loadErrors.length === 1 ? '' : 's'} para vista previa completa.
								{needsPut && missingHydrationCount > 0
									? ' No podés aplicar cambios de notas o proveedor hasta resolverlo.'
									: ' Los cambios de estado sí se pueden aplicar.'}
								{loadErrors.slice(0, 5).map((err) => {
									const ref = selection.find((item) => item.serviceId === err.serviceId)?.reference;
									return (
										<Typography key={`${err.serviceId}-${err.saleOrderId}`} variant="caption" display="block">
											{ref || err.serviceId || err.saleOrderId}: {err.message}
										</Typography>
									);
								})}
							</Alert>
						)}

						{loadingPreview ? (
							<Box display="flex" alignItems="center" gap={1} py={2}>
								<CircularProgress size={20} />
								<Typography variant="body2">Actualizando vista previa...</Typography>
							</Box>
						) : (
							<Box sx={{ overflowX: 'auto' }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Referencia</TableCell>
											<TableCell>SaleOrder</TableCell>
											{editInternalNotes && <TableCell>Notas internas</TableCell>}
											{editVoucherNotes && <TableCell>Notas voucher</TableCell>}
											{editProvider && <TableCell>Proveedor</TableCell>}
											{editEnable && <TableCell>Estado</TableCell>}
										</TableRow>
									</TableHead>
									<TableBody>
										{previewRows.map((row) => (
											<TableRow
												key={row.serviceId}
												sx={!row.hydrated && needsPut ? { bgcolor: 'warning.light', opacity: 0.9 } : undefined}
											>
												<TableCell>{row.reference}</TableCell>
												<TableCell>{row.saleOrderId}</TableCell>
												{editInternalNotes && (
													<TableCell>
														<Typography variant="caption" display="block" color="text.secondary">
															{row.internalNotesBefore}
														</Typography>
														<Typography variant="body2">→ {row.internalNotesAfter}</Typography>
													</TableCell>
												)}
												{editVoucherNotes && (
													<TableCell>
														<Typography variant="caption" display="block" color="text.secondary">
															{row.voucherNotesBefore}
														</Typography>
														<Typography variant="body2">→ {row.voucherNotesAfter}</Typography>
													</TableCell>
												)}
												{editProvider && (
													<TableCell>
														{row.tariffProviderBefore} → {row.tariffProviderAfter}
													</TableCell>
												)}
												{editEnable && (
													<TableCell>
														{row.enableBefore ? 'Activo' : 'Inactivo'} → {row.enableAfter ? 'Activo' : 'Inactivo'}
													</TableCell>
												)}
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						)}
					</Box>
				)}

				{step === 'executing' && (
					<Box>
						<Typography variant="body2" sx={{ mb: 1 }}>
							Guardando {progress.current} de {progress.total}...
						</Typography>
						<LinearProgress
							variant="determinate"
							value={progress.total ? (progress.current / progress.total) * 100 : 0}
						/>
					</Box>
				)}

				{step === 'done' && (
					<Box>
						<Alert severity={errorCount === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
							{successCount} actualizados correctamente
							{errorCount > 0 ? ` · ${errorCount} con error` : ''}
						</Alert>
						{results
							.filter((item) => item.status === 'error')
							.map((item) => (
								<Typography key={item.serviceId} variant="body2" color="error" sx={{ mb: 0.5 }}>
									{item.reference || item.serviceId}: {item.error}
								</Typography>
							))}
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				{step === 'form' && (
					<>
						<Button onClick={onClose}>Cancelar</Button>
						<Button variant="contained" onClick={handleGoToPreview} disabled={!formValid || loadingPreview}>
							Ver vista previa
						</Button>
					</>
				)}
				{step === 'preview' && (
					<>
						<Button onClick={() => setStep('form')}>Volver</Button>
						<Button variant="contained" onClick={handleExecute} disabled={!canApply}>
							Aplicar cambios
						</Button>
					</>
				)}
				{step === 'done' && <Button onClick={onClose}>Cerrar</Button>}
			</DialogActions>
		</Dialog>
	);
};
